import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import moment from "moment";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import { Select, Textarea } from "flowbite-react";
import {
  HiOutlineArrowLeft,
  HiOutlineTrash,
  HiOutlineUserGroup,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineAdjustments,
  HiOutlineTag,
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineViewBoards,
  HiOutlineLink,
  HiCheck,
  HiX,
  HiChevronDown,
} from "react-icons/hi";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  useGetTodoBoard,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  isBoardVerdict,
  boardKey,
} from "../../../../hooks/todos";
import { useResolveAssignees } from "../../../../hooks/todos/useResolveAssignees";
import {
  useDashboardAccessByRole,
  isTeamMemberRole,
} from "../../../../hooks/roles";
import {
  TodoTask,
  UpdateTaskInput,
  todoTaskLimit,
  TodoBoard as TodoBoardData,
  normalizeBoardAccess,
} from "../../../../types/TodoTypes";
import { CustomButton } from "../../../atoms/CustomButton";
import { ToolbarButton, ToolbarDivider } from "../../../atoms/ToolbarButton";
import { useCopyToClipboard } from "../../../atoms/useCopyToClipboard";
import { EmptyState } from "../../../atoms/EmptyState";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import UserAvatar from "../../../atoms/UserAvatar";
import NoTranslate from "../../../atoms/NoTranslate";
import ConfirmModal from "../../../public/ConfirmModal";
import {
  KanbanCard,
  KanbanColumn,
  KanbanDnd,
  useKanbanDndEnabled,
} from "../../../molecules/KanbanDnd";
import type { KanbanDndTexts } from "../../../molecules/KanbanDnd";
import TaskDetailModal from "./TaskDetailModal";
import BoardAccessBadge from "./BoardAccessBadge";
import TodoColumnsConfigModal from "./TodoColumnsConfigModal";
import BadgesConfigModal from "./BadgesConfigModal";
import PersonFilter from "./PersonFilter";
import BadgeFilter from "./BadgeFilter";
import { PickedUser } from "./AssigneePicker";
import { resolveColumnId, isHtmlEmpty, htmlToText } from "./todoColumns";
import { resolveBadges } from "./todoBadges";
import BadgePill from "./BadgePill";
import {
  TODO_FILTER_PARAMS,
  absoluteTaskUrl,
  boardPath,
  formatShortId,
  normalizeShortId,
  parseIdList,
  serializeIdList,
  taskPath,
} from "./todoUrl";

interface Props {
  boardId: string;
  shortId?: string;
  onBack: () => void;
}

const isOverdue = (task: TodoTask): boolean =>
  !!task.due_at && moment(task.due_at).isBefore(moment().startOf("day"));

const SEARCH_DEBOUNCE_MS = 350;

const TodoBoard: React.FC<Props> = ({ boardId, shortId, onBack }) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const { data: accessByRole } = useDashboardAccessByRole();
  const entityId = user.entity_identification;
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const createTask = useCreateTask(customer_identification, entityId);
  const { mutate: updateTask, isLoading: isSavingDetail } = useUpdateTask(
    customer_identification,
    entityId
  );
  const { mutate: deleteTask, isLoading: isDeleting } = useDeleteTask(
    customer_identification,
    entityId
  );

  const [isDragging, setIsDragging] = useState(false);
  const pollingPaused =
    isDragging || isSavingDetail || isDeleting || createTask.isLoading;

  const {
    data: board,
    isLoading,
    isError,
    error,
    isPreviousData,
  } = useGetTodoBoard(customer_identification, entityId, boardId, {
    pollingPaused,
  });

  const boardDenied = isError && isBoardVerdict(error);

  const [addingInColumn, setAddingInColumn] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [showColumns, setShowColumns] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<TodoTask | null>(null);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  const { status: copyStatus, copy: copyBoardLink } = useCopyToClipboard({
    onSuccess: () => toast.success(t("todos.share.copied") as string),
    onError: () => toast.error(t("common.copyFailed") as string),
  });
  const [collapsedColumns, setCollapsedColumns] = useState<
    Record<string, boolean>
  >({});
  const toggleColumnCollapse = (colId: string) =>
    setCollapsedColumns((prev) => ({ ...prev, [colId]: !prev[colId] }));

  const searchTerm = searchParams.get(TODO_FILTER_PARAMS.query) ?? "";
  const overdueOnly = searchParams.get(TODO_FILTER_PARAMS.overdue) === "1";
  const personFilterIds = useMemo(
    () => parseIdList(searchParams.get(TODO_FILTER_PARAMS.people)),
    [searchParams]
  );
  const badgeFilterIds = useMemo(
    () => parseIdList(searchParams.get(TODO_FILTER_PARAMS.badges)),
    [searchParams]
  );

  const setFilterParams = useCallback(
    (patch: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(searchTerm);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSearchRef = useRef(false);

  const clearPendingSearch = useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = null;
    pendingSearchRef.current = false;
  }, []);

  useEffect(() => () => clearPendingSearch(), [clearPendingSearch]);

  useEffect(() => {
    if (pendingSearchRef.current) return;
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    pendingSearchRef.current = true;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      pendingSearchRef.current = false;
      searchTimerRef.current = null;
      setFilterParams({ [TODO_FILTER_PARAMS.query]: value.trim() });
    }, SEARCH_DEBOUNCE_MS);
  };

  const clearSearch = () => {
    clearPendingSearch();
    setSearchInput("");
    setFilterParams({ [TODO_FILTER_PARAMS.query]: "" });
  };

  const columns = useMemo(() => board?.columns ?? [], [board]);

  const didSeedCollapse = useRef(false);
  useEffect(() => {
    if (didSeedCollapse.current || columns.length === 0) return;
    didSeedCollapse.current = true;
    setCollapsedColumns(Object.fromEntries(columns.map((c) => [c.id, true])));
  }, [columns]);

  const badges = board?.badges ?? [];
  const allTasks = useMemo(() => board?.tasks ?? [], [board]);
  const taskLimit = todoTaskLimit(permissions.getPlanTier());
  const atTaskCap = allTasks.length >= taskLimit;
  const openPlans = () => window.dispatchEvent(new Event("openPlansModal"));
  const tasks = useMemo(() => {
    let list = overdueOnly ? allTasks.filter(isOverdue) : allTasks;
    if (personFilterIds.length > 0) {
      list = list.filter((task) =>
        personFilterIds.some((id) => (task.user_ids || []).includes(id))
      );
    }
    if (badgeFilterIds.length > 0) {
      list = list.filter((task) =>
        badgeFilterIds.some((id) => (task.badge_ids || []).includes(id))
      );
    }
    const rawTerm = searchTerm.trim();
    const term = rawTerm.toLowerCase();
    if (term) {
      const shortIdTerm = normalizeShortId(rawTerm.replace(/^#/, ""));
      list = list.filter((task) => {
        const haystack = [
          task.title || "",
          htmlToText(task.description),
          ...(task.notes || []).map((n) => n.text || ""),
        ]
          .join(" ")
          .toLowerCase();
        if (haystack.includes(term)) return true;
        return (
          !!shortIdTerm && normalizeShortId(task.short_id).includes(shortIdTerm)
        );
      });
    }
    return list;
  }, [allTasks, overdueOnly, personFilterIds, badgeFilterIds, searchTerm]);
  const overdueCount = useMemo(
    () => allTasks.filter(isOverdue).length,
    [allTasks]
  );

  const hasActiveFilters =
    overdueOnly ||
    personFilterIds.length > 0 ||
    badgeFilterIds.length > 0 ||
    searchTerm.trim().length > 0;

  const toggleOverdueOnly = () =>
    setFilterParams({ [TODO_FILTER_PARAMS.overdue]: overdueOnly ? "" : "1" });
  const setPersonFilterIds = (ids: string[]) =>
    setFilterParams({ [TODO_FILTER_PARAMS.people]: serializeIdList(ids) });
  const setBadgeFilterIds = (ids: string[]) =>
    setFilterParams({ [TODO_FILTER_PARAMS.badges]: serializeIdList(ids) });

  const clearAllFilters = () => {
    setSearchInput("");
    clearPendingSearch();
    setFilterParams({
      [TODO_FILTER_PARAMS.query]: "",
      [TODO_FILTER_PARAMS.overdue]: "",
      [TODO_FILTER_PARAMS.people]: "",
      [TODO_FILTER_PARAMS.badges]: "",
    });
  };

  const { allUserIds, allGroupIds } = useMemo(() => {
    const u = new Set<string>();
    const g = new Set<string>();
    allTasks.forEach((task) => {
      (task.user_ids || []).forEach((id) => u.add(id));
      (task.group_ids || []).forEach((id) => g.add(id));
    });
    return { allUserIds: Array.from(u), allGroupIds: Array.from(g) };
  }, [allTasks]);

  const userIdsToResolve = useMemo(
    () => Array.from(new Set([...allUserIds, ...personFilterIds])),
    [allUserIds, personFilterIds]
  );

  const { resolvedUsers, resolvedGroups, isResolving } = useResolveAssignees(
    customer_identification,
    entityId,
    userIdsToResolve,
    allGroupIds
  );

  const personFilterChips: PickedUser[] = useMemo(
    () =>
      personFilterIds.map(
        (id) =>
          resolvedUsers[id] ?? {
            id,
            name: isResolving
              ? (t("todos.filters.resolving") as string)
              : (t("todos.filterPerson.unknown") as string),
          }
      ),
    [personFilterIds, resolvedUsers, isResolving, t]
  );

  const boardUrlPath = boardPath(boardId);
  const currentSearch = location.search;
  const boardShareUrl = `${window.location.origin}${boardUrlPath}${location.search}`;

  const [fallbackTaskId, setFallbackTaskId] = useState<string | null>(null);
  const openedByNavigationRef = useRef(false);

  const taskDetail = useMemo(() => {
    if (shortId) {
      const wanted = normalizeShortId(shortId);
      return (
        allTasks.find((task) => normalizeShortId(task.short_id) === wanted) ??
        null
      );
    }
    if (fallbackTaskId) {
      return allTasks.find((task) => task.task_id === fallbackTaskId) ?? null;
    }
    return null;
  }, [allTasks, shortId, fallbackTaskId]);

  const openTaskDetail = (task: TodoTask) => {
    const shortRef = normalizeShortId(task.short_id);
    if (!shortRef) {
      setFallbackTaskId(task.task_id);
      return;
    }
    setFallbackTaskId(null);
    openedByNavigationRef.current = true;
    navigate({ pathname: taskPath(boardId, shortRef), search: currentSearch });
  };

  const closeTaskDetail = () => {
    if (!shortId) {
      setFallbackTaskId(null);
      return;
    }
    if (openedByNavigationRef.current) {
      openedByNavigationRef.current = false;
      navigate(-1);
      return;
    }
    navigate(
      { pathname: boardUrlPath, search: currentSearch },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!shortId || isLoading || isPreviousData || !board) return;
    const wanted = normalizeShortId(shortId);
    if (allTasks.some((task) => normalizeShortId(task.short_id) === wanted))
      return;
    toast.error(t("todos.share.taskNotFound") as string);
    openedByNavigationRef.current = false;
    navigate(
      { pathname: boardUrlPath, search: currentSearch },
      { replace: true }
    );
  }, [shortId, isLoading, isPreviousData, board, allTasks]);

  const currentUserId = user.user_identification;
  const taskConcernsMe = (task: TodoTask) =>
    !!currentUserId && (task.user_ids || []).includes(currentUserId);

  const canWriteTasks = permissions.can(Cap.TASKS_BOARD_TASKS, Level.WRITE);

  const boardAccessUserIds = useMemo(() => {
    const normalized = normalizeBoardAccess(board?.access);
    return normalized.mode === "restricted" ? normalized.user_ids : undefined;
  }, [board?.access]);

  const dragEnabled = useKanbanDndEnabled() && canWriteTasks;

  const columnLabelById = useCallback(
    (columnId: string | null) =>
      columns.find((col) => col.id === columnId)?.label ?? "",
    [columns]
  );
  const taskTitleById = useCallback(
    (taskId: string) =>
      allTasks.find((item) => item.task_id === taskId)?.title ?? "",
    [allTasks]
  );

  const dndTexts = useMemo<KanbanDndTexts>(
    () => ({
      instructions: t("todos.dnd.instructions") as string,
      onDragStart: (taskId) =>
        t("todos.dnd.pickedUp", { title: taskTitleById(taskId) }) as string,
      onDragOver: (taskId, columnId) =>
        (columnId
          ? t("todos.dnd.over", {
              title: taskTitleById(taskId),
              column: columnLabelById(columnId),
            })
          : t("todos.dnd.outside", { title: taskTitleById(taskId) })) as string,
      onDragEnd: (taskId, columnId) =>
        (columnId
          ? t("todos.dnd.dropped", {
              title: taskTitleById(taskId),
              column: columnLabelById(columnId),
            })
          : t("todos.dnd.cancelled", {
              title: taskTitleById(taskId),
            })) as string,
      onDragCancel: (taskId) =>
        t("todos.dnd.cancelled", { title: taskTitleById(taskId) }) as string,
    }),
    [t, taskTitleById, columnLabelById]
  );

  const handleColumnChange = useCallback(
    (task: TodoTask, columnId: string) => {
      if (!canWriteTasks) return;
      if (columnId === resolveColumnId(task.column_id, columns)) return;
      if (updatingTaskId === task.task_id) return;

      const cacheKey = boardKey(entityId, boardId);
      // Une lecture deja en vol (poll, retour de focus) se resoudrait apres
      // notre ecriture et la remplacerait par l'etat d'avant le deplacement.
      // Rien ne resynchronise ensuite avant le poll suivant, donc on l'annule.
      queryClient.cancelQueries(cacheKey);
      const previousBoard = queryClient.getQueryData<TodoBoardData>(cacheKey);
      queryClient.setQueryData<TodoBoardData | undefined>(
        cacheKey,
        (current) =>
          current && {
            ...current,
            tasks: (current.tasks ?? []).map((item) =>
              item.task_id === task.task_id
                ? { ...item, column_id: columnId }
                : item
            ),
          }
      );

      setUpdatingTaskId(task.task_id);
      updateTask(
        { boardId, taskId: task.task_id, patch: { column_id: columnId } },
        {
          onError: (err) => {
            if (previousBoard) {
              queryClient.setQueryData<TodoBoardData>(cacheKey, previousBoard);
            } else {
              queryClient.invalidateQueries(cacheKey);
            }
            toast.error(
              (err?.response?.data?.message || t("todos.saveError")) as string
            );
          },
          onSettled: () => setUpdatingTaskId(null),
        }
      );
    },
    [
      boardId,
      canWriteTasks,
      columns,
      entityId,
      queryClient,
      t,
      updateTask,
      updatingTaskId,
    ]
  );

  /** Adaptateur pour KanbanDnd, qui ne connait que l'id de la carte deposee. */
  const moveTaskToColumn = useCallback(
    (taskId: string, columnId: string) => {
      const task = allTasks.find((item) => item.task_id === taskId);
      if (task) handleColumnChange(task, columnId);
    },
    [allTasks, handleColumnChange]
  );

  const handleDetailSave = (patch: UpdateTaskInput, done: () => void) => {
    if (!taskDetail) return;
    updateTask(
      { boardId, taskId: taskDetail.task_id, patch },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(boardKey(entityId, boardId));
          done();
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message || t("todos.saveError")) as string
          );
        },
      }
    );
  };

  const openComposer = (columnId: string) => {
    setAddingInColumn(columnId);
    setNewTaskTitle("");
  };
  const closeComposer = () => {
    setAddingInColumn(null);
    setNewTaskTitle("");
  };

  const createTaskInColumn = (columnId: string) => {
    if (!permissions.can(Cap.TASKS_BOARD_TASKS, Level.WRITE)) return;
    const title = newTaskTitle.trim();
    if (!title || createTask.isLoading) return;
    createTask.mutate(
      { boardId, input: { title, column_id: columnId } },
      {
        onSuccess: () => {
          setNewTaskTitle("");
        },
        onError: (err) => {
          toast.error(
            (err?.response?.data?.message || t("todos.saveError")) as string
          );
        },
      }
    );
  };

  const handleDelete = () => {
    if (!permissions.can(Cap.TASKS_BOARD_TASKS, Level.DELETE)) return;
    if (!taskToDelete) return;
    deleteTask(
      { boardId, taskId: taskToDelete.task_id },
      { onSettled: () => setTaskToDelete(null) }
    );
  };

  const assigneeAvatars = (task: TodoTask) => {
    const users = (task.user_ids || [])
      .map((id) => resolvedUsers[id])
      .filter(Boolean);
    const groups = (task.group_ids || [])
      .map((id) => resolvedGroups[id])
      .filter(Boolean);
    if (users.length === 0 && groups.length === 0) return null;
    return (
      <div className="flex items-center gap-1 flex-wrap mt-1">
        {users.slice(0, 4).map((u) => (
          <span
            key={u.id}
            title={
              isTeamMemberRole(u.role, accessByRole)
                ? `${u.name} · ${t("todos.task.operatorTag")}`
                : u.name
            }
            className={
              isTeamMemberRole(u.role, accessByRole)
                ? "ring-2 ring-blue-300 rounded-full"
                : ""
            }
          >
            <UserAvatar
              firstName={u.firstName}
              lastName={u.lastName}
              email={u.email}
              avatar={u.avatar}
              sizeClass="w-6 h-6"
              textClass="text-xs"
            />
          </span>
        ))}
        {users.length > 4 && (
          <span className="text-xs text-gray-400">+{users.length - 4}</span>
        )}
        {groups.slice(0, 2).map((g) => (
          <span
            key={g.id}
            title={g.name}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600 text-xs"
          >
            <HiOutlineUserGroup size={11} />
            <span className="max-w-[60px] truncate">{g.name}</span>
          </span>
        ))}
      </div>
    );
  };

  const taskCardClass =
    "group bg-white rounded-lg border border-gray-200 p-3 flex flex-col gap-1.5 cursor-pointer transition-colors hover:border-gray-300";

  const taskCardBody = (task: TodoTask) => {
    const taskBadges = resolveBadges(task.badge_ids, badges);
    const shortRef = formatShortId(task.short_id);
    return (
      <>
        <div className="flex items-start justify-between gap-2">
          <span
            className="text-sm font-medium text-gray-900 break-words min-w-0 flex items-start gap-1.5"
            title={task.title}
          >
            {taskConcernsMe(task) && (
              <span
                className="mt-1 w-2 h-2 rounded-full bg-blue-500 shrink-0"
                title={t("todos.task.concernsMe") as string}
                aria-label={t("todos.task.concernsMe") as string}
              />
            )}
            <NoTranslate>{task.title}</NoTranslate>
          </span>
          {permissions.can(Cap.TASKS_BOARD_TASKS, Level.DELETE) && (
            <span data-dnd-ignore className="shrink-0">
              <CustomButton
                type="button"
                variant="none"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setTaskToDelete(task);
                }}
                disabled={isDeleting}
                className="rounded-md text-gray-400 transition-opacity enabled:hover:text-red-600 md:opacity-0 md:group-hover:opacity-100"
                icon={<HiOutlineTrash size={18} />}
                aria-label={t("common.delete") as string}
              />
            </span>
          )}
        </div>

        {taskBadges.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {taskBadges.map((b) => (
              <BadgePill key={b.id} badge={b} size="sm" />
            ))}
          </div>
        )}

        {assigneeAvatars(task)}
        {task.due_at && (
          <span
            className={`inline-flex items-center gap-1 text-xs w-fit ${
              isOverdue(task) ? "text-red-600 font-medium" : "text-gray-500"
            }`}
          >
            <HiOutlineCalendar
              size={13}
              className={isOverdue(task) ? "text-red-500" : "text-gray-400"}
            />
            {moment(task.due_at).format("ll")}
            {isOverdue(task) && (
              <span className="text-xs uppercase tracking-wide">
                · {t("todos.task.overdue")}
              </span>
            )}
          </span>
        )}
        {(shortRef ||
          !isHtmlEmpty(task.description) ||
          (task.notes && task.notes.length > 0)) && (
          <div className="flex items-center gap-2.5 text-xs text-gray-400">
            {shortRef && (
              <span
                data-dnd-ignore
                className="cursor-text select-all tabular-nums"
                onClick={(e) => e.stopPropagation()}
                title={t("todos.share.taskIdLabel") as string}
              >
                <NoTranslate>{shortRef}</NoTranslate>
              </span>
            )}
            {!isHtmlEmpty(task.description) && (
              <span
                className="inline-flex items-center gap-0.5"
                title={t("todos.task.hasDescription") as string}
              >
                <HiOutlineDocumentText size={13} />
              </span>
            )}
            {task.notes && task.notes.length > 0 && (
              <span className="inline-flex items-center gap-1">
                {t("todos.task.noteCount", { count: task.notes.length })}
              </span>
            )}
          </div>
        )}

        {!dragEnabled && (
          <div
            data-dnd-ignore
            className="mt-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              sizing="sm"
              value={resolveColumnId(task.column_id, columns)}
              disabled={updatingTaskId === task.task_id || !canWriteTasks}
              aria-label={
                t("todos.task.changeColumn", { title: task.title }) as string
              }
              onChange={(e) => handleColumnChange(task, e.target.value)}
            >
              {columns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </div>
        )}
      </>
    );
  };

  const taskCard = (task: TodoTask, columnId: string) => (
    <KanbanCard
      key={task.task_id}
      cardId={task.task_id}
      columnId={columnId}
      disabled={updatingTaskId === task.task_id}
      className={taskCardClass}
      ariaLabel={
        t("todos.dnd.cardLabel", {
          title: task.title,
          column: columnLabelById(columnId),
        }) as string
      }
      onActivate={() => openTaskDetail(task)}
    >
      {taskCardBody(task)}
    </KanbanCard>
  );

  /** La carte qui suit le pointeur : meme habillage, sans draggable propre. */
  const taskDragPreview = (taskId: string) => {
    const task = allTasks.find((item) => item.task_id === taskId);
    if (!task) return null;
    return (
      <div className={twMerge(taskCardClass, "cursor-grabbing shadow-lg")}>
        {taskCardBody(task)}
      </div>
    );
  };

  const gridColsClass =
    [
      "",
      "xl:grid-cols-1",
      "xl:grid-cols-2",
      "xl:grid-cols-3",
      "xl:grid-cols-4",
      "xl:grid-cols-5",
      "xl:grid-cols-6",
      "xl:grid-cols-6",
      "xl:grid-cols-6",
    ][Math.min(columns.length, 8)] || "xl:grid-cols-5";

  if (!isLoading && !isPreviousData && (!board || boardDenied)) {
    return (
      <div className="container mx-auto py-6 px-3">
        <EmptyState
          bordered
          icon={<HiOutlineViewBoards size={24} />}
          title={t("todos.boardNotFound.title")}
          description={t("todos.boardNotFound.description")}
          action={
            <CustomButton
              variant="secondary"
              size="sm"
              onClick={onBack}
              title={t("todos.boardNotFound.back") as string}
            />
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-3">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <CustomButton
            type="button"
            variant="none"
            size="icon"
            onClick={onBack}
            className="-ml-1.5 shrink-0 rounded-md text-gray-400 enabled:hover:text-gray-700"
            icon={<HiOutlineArrowLeft size={20} />}
            aria-label={t("common.back") as string}
          />
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold text-gray-900">
              <NoTranslate>{board?.name || ""}</NoTranslate>
            </h1>
            {!isLoading && (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-500">
                <span>{t("todos.taskCount", { count: allTasks.length })}</span>
                <BoardAccessBadge access={board?.access} />
                {isError && !boardDenied && (
                  <span className="text-amber-600">
                    {t("todos.refreshFailed")}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-x-4 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm border bg-white border-gray-300 text-gray-600">
            <HiOutlineSearch size={16} className="text-gray-400 shrink-0" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("todos.search.placeholder") as string}
              className="w-40 sm:w-64 border-0 p-0 text-sm focus:ring-0 placeholder:text-gray-400"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                className="text-gray-400 hover:text-red-600 shrink-0"
                aria-label={t("todos.search.clear") as string}
              >
                <HiX size={14} />
              </button>
            )}
          </div>

          <CustomButton
            type="button"
            variant={overdueOnly ? "custom" : "secondary"}
            size="sm"
            aria-pressed={overdueOnly}
            disabled={overdueCount === 0 && !overdueOnly}
            tooltip={
              overdueCount === 0
                ? (t("todos.filters.noOverdue") as string)
                : undefined
            }
            className={
              overdueOnly
                ? "border border-red-300 bg-red-50 text-red-700 enabled:hover:bg-red-100"
                : undefined
            }
            onClick={toggleOverdueOnly}
            comp={
              <span className="flex items-center gap-1.5">
                <HiOutlineCalendar size={16} className="shrink-0" />
                {t("todos.filterOverdue", { count: overdueCount })}
              </span>
            }
          />

          <PersonFilter
            customerId={customer_identification}
            entityId={entityId}
            selectedIds={personFilterIds}
            onAdd={(p) => {
              if (personFilterIds.includes(p.id)) return;
              setPersonFilterIds([...personFilterIds, p.id]);
            }}
          />

          <BadgeFilter
            badges={badges}
            selectedIds={badgeFilterIds}
            onToggle={(id) =>
              setBadgeFilterIds(
                badgeFilterIds.includes(id)
                  ? badgeFilterIds.filter((x) => x !== id)
                  : [...badgeFilterIds, id]
              )
            }
          />
        </div>

        <div className="flex items-center justify-end gap-1 sm:ml-auto">
          <ToolbarButton
            disabled={copyStatus === "copying"}
            aria-label={t("todos.share.board") as string}
            className={twMerge(
              copyStatus === "success" && "text-green-600",
              copyStatus === "error" && "text-red-600"
            )}
            onClick={() => copyBoardLink(boardShareUrl)}
            icon={
              copyStatus === "success" ? (
                <HiCheck size={16} className="shrink-0" />
              ) : (
                <HiOutlineLink size={16} className="shrink-0" />
              )
            }
            title={t("todos.share.board")}
          />

          {permissions.can(Cap.TASKS_BOARD_CONFIG, Level.WRITE) && (
            <>
              <ToolbarDivider />
              <ToolbarButton
                aria-label={t("todos.badges.configure") as string}
                onClick={() => setShowBadges(true)}
                icon={<HiOutlineTag size={16} className="shrink-0" />}
                title={t("todos.badges.configure")}
              />
              <ToolbarButton
                aria-label={t("todos.columns.configure") as string}
                onClick={() => setShowColumns(true)}
                icon={<HiOutlineAdjustments size={16} className="shrink-0" />}
                title={t("todos.columns.configure")}
              />
            </>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className="flex items-center gap-1.5 flex-wrap mb-4">
          <span className="text-xs text-gray-400 mr-0.5">
            {t("todos.filters.active")}
          </span>

          {searchTerm.trim() && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-50 border-gray-200 text-gray-600">
              <HiOutlineSearch size={12} />
              <span className="max-w-[140px] truncate">
                “{searchTerm.trim()}”
              </span>
              <button
                type="button"
                onClick={clearSearch}
                className="hover:text-red-600"
                aria-label={t("todos.search.clear") as string}
              >
                <HiX size={12} />
              </button>
            </span>
          )}

          {overdueOnly && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-red-50 border-red-200 text-red-700">
              <HiOutlineCalendar size={12} />
              {t("todos.filterOverdue", { count: overdueCount })}
              <button
                type="button"
                onClick={toggleOverdueOnly}
                className="hover:text-red-800"
                aria-label={t("todos.filters.clearAll") as string}
              >
                <HiX size={12} />
              </button>
            </span>
          )}

          {personFilterChips.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium border bg-blue-50 border-blue-200 text-blue-700"
            >
              <UserAvatar
                firstName={p.firstName}
                lastName={p.lastName}
                email={p.email}
                avatar={p.avatar}
                sizeClass="w-4 h-4"
                textClass="text-xs"
              />
              <span className="max-w-[110px] truncate">{p.name}</span>
              <button
                type="button"
                onClick={() =>
                  setPersonFilterIds(
                    personFilterIds.filter((id) => id !== p.id)
                  )
                }
                className="hover:text-red-600"
                aria-label={t("todos.filterPerson.clear") as string}
              >
                <HiX size={12} />
              </button>
            </span>
          ))}

          {resolveBadges(badgeFilterIds, badges).map((b) => (
            <BadgePill
              key={b.id}
              badge={b}
              size="md"
              onRemove={() =>
                setBadgeFilterIds(badgeFilterIds.filter((x) => x !== b.id))
              }
              removeLabel={t("todos.filterBadge.clear") as string}
            />
          ))}

          <button
            type="button"
            onClick={clearAllFilters}
            className="ml-1 text-xs text-gray-500 hover:text-red-600 underline"
          >
            {t("todos.filters.clearAll")}
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-center text-gray-400 py-16">
          {t("todos.loading")}
        </div>
      ) : hasActiveFilters && tasks.length === 0 ? (
        <EmptyState
          bordered
          icon={<HiOutlineCalendar size={24} />}
          title={
            overdueOnly && overdueCount === 0
              ? t("todos.filters.noOverdue")
              : t("todos.filters.noResults")
          }
          action={
            <CustomButton
              variant="secondary"
              size="sm"
              onClick={clearAllFilters}
              title={t("todos.filters.clearAll") as string}
            />
          }
        />
      ) : (
        <KanbanDnd
          disabled={!canWriteTasks}
          onMoveCard={moveTaskToColumn}
          onDraggingChange={setIsDragging}
          renderDragOverlay={taskDragPreview}
          texts={dndTexts}
        >
          <div
            className={`grid grid-cols-1 md:grid-cols-2 ${gridColsClass} gap-4`}
          >
            {columns.map((col) => {
              const colTasks = tasks.filter(
                (task) => resolveColumnId(task.column_id, columns) === col.id
              );
              const collapsed = collapsedColumns[col.id];
              return (
                <KanbanColumn
                  key={col.id}
                  columnId={col.id}
                  className="bg-gray-50 rounded-lg p-3 md:min-h-[200px]"
                >
                  <button
                    type="button"
                    onClick={() => toggleColumnCollapse(col.id)}
                    className="w-full flex items-center justify-between mb-3 md:cursor-default"
                    aria-expanded={!collapsed}
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <HiChevronDown
                        size={16}
                        className={`text-gray-400 shrink-0 md:hidden transition-transform ${
                          collapsed ? "-rotate-90" : ""
                        }`}
                      />
                      <span className="truncate text-sm font-semibold text-gray-900">
                        <NoTranslate>{col.label}</NoTranslate>
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-medium tabular-nums text-gray-500">
                      <NoTranslate>{colTasks.length}</NoTranslate>
                    </span>
                  </button>
                  <div
                    className={`flex-col gap-2 ${
                      collapsed ? "hidden md:flex" : "flex"
                    }`}
                  >
                    {colTasks.map((task) => taskCard(task, col.id))}

                    {addingInColumn === col.id ? (
                      <div className="bg-white rounded-lg border border-blue-300 p-2">
                        <Textarea
                          value={newTaskTitle}
                          rows={2}
                          autoFocus
                          placeholder={t("todos.addTask.titleLabel") as string}
                          maxLength={150}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              createTaskInColumn(col.id);
                            }
                            if (e.key === "Escape") closeComposer();
                          }}
                          onBlur={() => {
                            if (!newTaskTitle.trim()) closeComposer();
                          }}
                          className="w-full resize-none"
                        />
                        <div className="flex items-center justify-end gap-2 mt-1.5">
                          <CustomButton
                            variant="secondary"
                            size="xs"
                            title={t("common.cancel") as string}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={closeComposer}
                          />
                          <CustomButton
                            variant="primary"
                            size="xs"
                            disabled={
                              !newTaskTitle.trim() || createTask.isLoading
                            }
                            isLoading={createTask.isLoading}
                            title={t("common.add") as string}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => createTaskInColumn(col.id)}
                          />
                        </div>
                      </div>
                    ) : atTaskCap ? (
                      <button
                        type="button"
                        onClick={openPlans}
                        className="w-full flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-amber-50 transition-colors"
                        title={
                          t("todos.taskLimitReached", {
                            limit: taskLimit,
                          }) as string
                        }
                      >
                        <UnlockBadge onClick={openPlans} />
                      </button>
                    ) : (
                      permissions.can(Cap.TASKS_BOARD_TASKS, Level.WRITE) && (
                        <CustomButton
                          type="button"
                          variant="none"
                          size="xs"
                          onClick={() => openComposer(col.id)}
                          className="w-full justify-start gap-1.5 rounded-md px-2 py-1.5 text-xs text-gray-500 enabled:hover:bg-gray-100 enabled:hover:text-gray-700"
                          icon={<HiOutlinePlus size={16} />}
                          title={t("todos.addTask.newTask")}
                        />
                      )
                    )}
                  </div>
                </KanbanColumn>
              );
            })}
          </div>
        </KanbanDnd>
      )}

      <TaskDetailModal
        task={taskDetail}
        columns={columns}
        badges={badges}
        customerId={customer_identification}
        entityId={entityId}
        boardId={boardId}
        resolvedUsers={resolvedUsers}
        resolvedGroups={resolvedGroups}
        shareUrl={
          taskDetail?.short_id
            ? absoluteTaskUrl(boardId, normalizeShortId(taskDetail.short_id))
            : undefined
        }
        boardAccessUserIds={boardAccessUserIds}
        onClose={closeTaskDetail}
        onSave={handleDetailSave}
        isSaving={isSavingDetail}
      />

      <TodoColumnsConfigModal
        show={showColumns}
        onClose={() => setShowColumns(false)}
        boardId={boardId}
        columns={columns}
      />

      <BadgesConfigModal
        show={showBadges}
        onClose={() => setShowBadges(false)}
        boardId={boardId}
        badges={badges}
      />

      <ConfirmModal
        show={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        title={t("todos.task.deleteConfirmTitle") as string}
        description={t("todos.task.deleteConfirmText") as string}
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={() => setTaskToDelete(null)}
        confirmBtnName={t("common.delete") as string}
        confirmBtnFn={handleDelete}
        isLoading={isDeleting}
        ConfirmBtnColor="red"
      />
    </div>
  );
};

export default TodoBoard;
